/**
 * Canvas-side layer components: snap helpers + draggable text + draggable photo.
 * Extracted from PostEditor.jsx.
 */
import { useEffect, useRef } from "react";
import { CANVAS_SNAP, SNAP_T, FONT_CSS_MAP } from "./constants";

// Hedef katmanlardan X ve Y snap listeleri çıkar. Layer'ın görsel sol/sağ/orta,
// üst/alt/orta kenarları hedef olur. Kendi katmanı atlanır.
function _collectSnapTargets(allLayers, selfId, canvasAR) {
  const xs = new Set(CANVAS_SNAP);
  const ys = new Set(CANVAS_SNAP);
  for (const l of allLayers) {
    if (!l || l.hidden || !l.text || l.id === selfId) continue;
    const w = Math.min(1, l.max_width || 0.6);
    let left = l.x, right = l.x;
    if (l.align === "center") { left = l.x - w / 2; right = l.x + w / 2; }
    else if (l.align === "right") { left = l.x - w; }
    else { right = l.x + w; }
    xs.add(left);
    xs.add(right);
    xs.add((left + right) / 2);
    const h = l.size || 0.07;
    const top = l.y;
    const bottom = l.y + h;
    ys.add(top);
    ys.add(bottom);
    ys.add((top + bottom) / 2);
  }
  void canvasAR;
  return { xs: [...xs], ys: [...ys] };
}

function _nearest(value, candidates, threshold = SNAP_T) {
  let best = null, bestDist = threshold;
  for (const c of candidates) {
    const d = Math.abs(value - c);
    if (d < bestDist) { bestDist = d; best = c; }
  }
  return best;
}

export function DraggableTextLayer({ layer, active, selectedIds, allLayers, canvasRef, onChange, onSelect, onMulti, onGuides, editing, onEditStart, onEditEnd }) {
  const dragRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editing]);

  const beginDrag = (e, mode) => {
    if (editing) return;
    if (layer.locked && mode === "move") return;
    e.preventDefault();
    e.stopPropagation();
    const shift = !!e.shiftKey;
    onSelect(layer.id, shift);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const selected = shift
      ? Array.from(new Set([...(selectedIds || []), layer.id]))
      : (selectedIds && selectedIds.length > 1 && selectedIds.includes(layer.id) ? selectedIds : [layer.id]);
    const startMap = {};
    for (const id of selected) {
      const l = allLayers.find((x) => x.id === id);
      if (l) startMap[id] = { x: l.x, y: l.y, size: l.size, max_width: l.max_width };
    }
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, start: { ...layer }, rect, selected, startMap };
    const move = (ev) => {
      const s = dragRef.current;
      if (!s) return;
      let dxN = (ev.clientX - s.startX) / s.rect.width;
      let dyN = (ev.clientY - s.startY) / s.rect.height;
      if (s.mode === "move" && ev.shiftKey) {
        if (Math.abs(dxN) > Math.abs(dyN)) dyN = 0;
        else dxN = 0;
      }
      if (s.mode === "move") {
        if (s.selected.length > 1) {
          onMulti(s.selected, dxN, dyN, s.startMap);
          onGuides({ v: null, h: null });
        } else {
          let nx = Math.max(0, Math.min(1, s.start.x + dxN));
          let ny = Math.max(0, Math.min(1, s.start.y + dyN));
          const { xs, ys } = _collectSnapTargets(allLayers, layer.id, s.rect.height / s.rect.width);
          const sv = ev.altKey ? null : _nearest(nx, xs);
          const sh = ev.altKey ? null : _nearest(ny, ys);
          const w = Math.min(1, s.start.max_width || 0.6);
          if (sv !== null) {
            if (layer.align === "center") nx = sv;
            else if (layer.align === "right") nx = sv;
            else nx = sv;
          }
          if (sh !== null) ny = sh;
          void w;
          onChange(layer.id, { x: nx, y: ny });
          onGuides({ v: sv, h: sh });
        }
      } else if (s.mode === "size") {
        const delta = (dyN + dxN) * 0.5;
        onChange(layer.id, {
          size: Math.max(0.02, Math.min(0.25, s.start.size + delta * 0.3)),
          _mwAuto: false,
        });
      } else if (s.mode === "width") {
        onChange(layer.id, {
          max_width: Math.max(0.1, Math.min(1, s.start.max_width + dxN)),
          _mwAuto: false,
        });
      }
    };
    const up = () => {
      dragRef.current = null;
      onGuides({ v: null, h: null });
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const style = {
    left: `${layer.x * 100}%`,
    top: `${layer.y * 100}%`,
    maxWidth: `${layer.max_width * 100}%`,
    textShadow: layer.shadow ? "0 2px 8px rgba(0,0,0,.55)" : "none",
    textAlign: layer.align,
    color: layer.color,
    cursor: active ? "move" : "pointer",
    touchAction: "none",
  };
  const fam = layer.font_family || "inter";
  const fontCss = {
    fontSize: `${layer.size * 100}cqh`,
    fontFamily: FONT_CSS_MAP[fam] || FONT_CSS_MAP.inter,
    fontWeight: layer.bold ? 800 : 400,
    fontStyle: layer.italic ? "italic" : "normal",
    textDecoration: layer.underline ? "underline" : "none",
    letterSpacing: layer.bold ? "-0.015em" : "-0.005em",
    lineHeight: 1.1,
  };

  return (
    <div className={`absolute group ${active ? "outline outline-2 outline-amber-400 outline-offset-2 rounded" : "hover:outline hover:outline-1 hover:outline-amber-300/60 hover:outline-offset-2 hover:rounded"} ${layer.locked ? "cursor-not-allowed" : ""}`}
         style={style}
         onPointerDown={(e) => beginDrag(e, "move")}
         onDoubleClick={(e) => { e.stopPropagation(); if (!layer.locked) onEditStart(layer.id); }}
         data-testid={`pe-layer-preview-${layer.id}`}>
      {editing ? (
        <textarea ref={textareaRef}
                  value={layer.text}
                  onChange={(e) => onChange(layer.id, { text: e.target.value })}
                  onBlur={() => onEditEnd()}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") { e.preventDefault(); onEditEnd(); }
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  rows={Math.max(1, layer.text.split("\n").length)}
                  className="bg-white/90 text-slate-900 border border-amber-500 rounded px-1 py-0 resize-none focus:outline-none shadow-lg"
                  style={{ ...fontCss, color: "#111", textAlign: layer.align, minWidth: 120, maxWidth: "100%" }}
                  data-testid={`pe-layer-edit-${layer.id}`} />
      ) : (
        <span className="whitespace-pre-wrap break-words block select-none" style={fontCss}>
          {layer.text}
        </span>
      )}
      {layer.locked && (
        <span className="absolute -top-2 -left-2 text-xs">🔒</span>
      )}
      {active && !editing && !layer.locked && (
        <>
          <button type="button" onPointerDown={(e) => beginDrag(e, "size")}
                  className="absolute -bottom-1.5 -right-1.5 w-3 h-3 rounded-full bg-white border-2 border-amber-500 shadow cursor-nwse-resize"
                  title="Boyut (sürükle)"
                  data-testid={`pe-layer-handle-size-${layer.id}`} />
          <button type="button" onPointerDown={(e) => beginDrag(e, "width")}
                  className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2 h-6 rounded bg-white border-2 border-amber-500 shadow cursor-ew-resize"
                  title="Genişlik (sürükle)"
                  data-testid={`pe-layer-handle-width-${layer.id}`} />
        </>
      )}
    </div>
  );
}

export function DraggablePhoto({ url, box, onChange, shape = "rect", canvasRef }) {
  const dragStateRef = useRef(null);

  const begin = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    dragStateRef.current = {
      mode, startX: e.clientX, startY: e.clientY,
      startBox: { ...box }, rect,
    };
    const move = (ev) => {
      const s = dragStateRef.current;
      if (!s) return;
      const dxN = (ev.clientX - s.startX) / s.rect.width;
      const dyN = (ev.clientY - s.startY) / s.rect.height;
      const next = { ...s.startBox };
      if (s.mode === "move") {
        next.x = Math.max(next.w / 2, Math.min(1 - next.w / 2, s.startBox.x + dxN));
        next.y = Math.max(next.h / 2, Math.min(1 - next.h / 2, s.startBox.y + dyN));
      } else {
        const sign = { tl: [-1, -1], tr: [1, -1], bl: [-1, 1], br: [1, 1] }[s.mode] || [1, 1];
        const minW = 0.05, minH = 0.05;
        let newW = Math.max(minW, Math.min(1, s.startBox.w + sign[0] * dxN));
        let newH = Math.max(minH, Math.min(1, s.startBox.h + sign[1] * dyN));
        if (ev.shiftKey) {
          const ar = s.startBox.w / s.startBox.h;
          if (Math.abs(dxN) > Math.abs(dyN)) newH = newW / ar;
          else newW = newH * ar;
        }
        next.w = newW;
        next.h = newH;
        next.x = s.startBox.x + sign[0] * (newW - s.startBox.w) / 2;
        next.y = s.startBox.y + sign[1] * (newH - s.startBox.h) / 2;
      }
      onChange(next);
    };
    const up = () => {
      dragStateRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const style = {
    left: `${(box.x - box.w / 2) * 100}%`,
    top: `${(box.y - box.h / 2) * 100}%`,
    width: `${box.w * 100}%`,
    height: `${box.h * 100}%`,
  };
  const handleStyle = "absolute w-3 h-3 rounded-full bg-white border-2 border-indigo-600 shadow hover:scale-125 transition";

  return (
    <div
      className="absolute cursor-move group ring-2 ring-indigo-400/60 hover:ring-indigo-500"
      style={style}
      onPointerDown={(e) => begin(e, "move")}
      data-testid="pe-photo-drag"
    >
      <img src={url} alt="" draggable={false}
           className="w-full h-full object-cover shadow-xl pointer-events-none select-none"
           style={{ borderRadius: shape === "circle" ? "50%" : "6px" }} />
      <button type="button" onPointerDown={(e) => begin(e, "tl")}
              className={`${handleStyle} -top-1.5 -left-1.5 cursor-nwse-resize`} data-testid="pe-photo-handle-tl" aria-label="Sol üst köşe" />
      <button type="button" onPointerDown={(e) => begin(e, "tr")}
              className={`${handleStyle} -top-1.5 -right-1.5 cursor-nesw-resize`} data-testid="pe-photo-handle-tr" aria-label="Sağ üst köşe" />
      <button type="button" onPointerDown={(e) => begin(e, "bl")}
              className={`${handleStyle} -bottom-1.5 -left-1.5 cursor-nesw-resize`} data-testid="pe-photo-handle-bl" aria-label="Sol alt köşe" />
      <button type="button" onPointerDown={(e) => begin(e, "br")}
              className={`${handleStyle} -bottom-1.5 -right-1.5 cursor-nwse-resize`} data-testid="pe-photo-handle-br" aria-label="Sağ alt köşe" />
    </div>
  );
}
